import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  CHANNEL_STATUS,
  CHANNEL_STATUS_OBJECT,
  CHANNEL_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
} from '../../../../../enum/constants';
import { NotificationService } from '../../../../../services/notifications.service';
import { ChannelService } from '../../../../../services/channel.service';
import { takeUntil } from 'rxjs';
import { BaseService } from '../../../../../services/base.service';
import { WebSocketService } from '../../../../../services/gateway.service';
import { ISocketIntegrationEvent, SocketIntegrationAction } from '../integration-instagram/integration.interface';
import { IntegrationService } from '../../../../../services/integration.service';
import { BaseIntegration } from '../base-integration/base-integration';

@Component({
  selector: 'app-integration-tiktok',
  templateUrl: './integration-tiktok.component.html',
  styleUrls: ['./integration-tiktok.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationTiktokComponent extends BaseIntegration implements OnInit, OnDestroy {
  protected xDataEntries: [string, any][] = [];

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    protected override readonly notificationService: NotificationService,
    protected override readonly channelService: ChannelService,
    protected override readonly integrationService: IntegrationService,
    protected readonly baseService: BaseService,
    protected readonly webSocketService: WebSocketService,
  ) {
    super(integrationService, notificationService, channelService);
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.channel_id = this.payload.channel_id;
    this.initSockets();
    const handled = this.handleLoginRedirect();
    if (!handled) {
      this.getChannelById();
    }
  }

  private initSockets() {
    this.webSocketService
      .instAuthAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.handleAuthSocketActions(data);
        },
      });
  }

  protected connectAccount() {
    const redirectUrl = window.location.href;
    this.isLoader = true;
    const request$ = this.baseService.company.settings.is_tiktok_v2
      ? this.integrationService.getTiktokLink(this.channel_id, redirectUrl)
      : this.integrationService.getTiktokLoginUrl(this.channel_id, redirectUrl);
    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        const loginUrl = data?.url;
        if (!loginUrl) {
          this.isLoader = false;
          this.notificationService.showError('', DEFAULT_ERROR);
          return;
        }
        this.qrUrl = loginUrl;
        if (this.baseService.company.settings.is_tiktok_v2) {
          window.open(loginUrl, '_blank');
          this.isLoader = false;
        } else {
          window.location.href = loginUrl;
        }
      },
      error: (e) => {
        this.isLoader = false;
        const errors = e.error?.errors || [];
        if (errors.length) {
          errors.forEach((err: any) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
          });
          return;
        }
        this.notificationService.showError('', DEFAULT_ERROR);
      },
    });
  }

  private handleAuthSocketActions(event: ISocketIntegrationEvent) {
    if (this.channel_id !== event.channel_id) return;

    switch (event.action) {
      case SocketIntegrationAction.PROCESSED: {
        this.isLoader = true;
        this.showQr = false;
        break;
      }
      case SocketIntegrationAction.SUCCESS: {
        this.getChannelById();
        this.showQr = false;
        break;
      }

      case SocketIntegrationAction.FAILED: {
        this.isLoader = false;
        this.showQr = false;
        if (event.error) {
          this.notificationService.showError('', ERROR_MESSAGES[event.error] || DEFAULT_ERROR);
        }
        break;
      }
    }
  }

  protected readonly CHANNEL_STATUS = CHANNEL_STATUS;
  protected readonly CHANNEL_STATUS_OBJECT = CHANNEL_STATUS_OBJECT;

  protected override deleteChannel(): void {
    this.isLoader = true;
    this.integrationService
      .deleteTiktok(this.channel_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.getChannelById();
        },
        error: (e) => {
          (e.error?.errors || []).forEach((err: any) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
          });
          this.isLoader = false;
        },
      });
  }

  private handleLoginRedirect(): boolean {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const integration = params.get('integration');
    if (integration !== CHANNEL_TYPE.TIKTOK || !action) return false;

    if (action === 'SUCCESS') {
      this.getChannelById();
    } else if (action === 'FAILED') {
      const error = params.get('error');
      const isDuplicate =
        error === 'INTEGRATION_ALREADY_EXISTS' || error === 'INTEGRATION_ALREADY_EXISTS_IN_ANOTHER_COMPANY';
      this.notificationService.showError('', ERROR_MESSAGES[error || ''] || DEFAULT_ERROR);
      if (!isDuplicate) {
        this.getChannelById();
      }
    } else {
      this.notificationService.showError('', DEFAULT_ERROR);
      this.getChannelById();
    }
    this.clearRedirectParams(params);
    return true;
  }

  private clearRedirectParams(params: URLSearchParams) {
    ['action', 'integration', 'open_id', 'scope', 'code', 'state', 'channel_id'].forEach((k) => params.delete(k));
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }

  protected override channelByIdCallback(): void {
    this.xDataEntries = this.channel?.x_data ? Object.entries(this.channel.x_data) : [];
  }

  protected trackByXDataKey(_index: number, item: [string, any]): string {
    return item[0];
  }

  protected readonly Object = Object;

  protected isObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }
}
